/**
 * Session Logger Service
 * Handles session logging for analytics and user tracking
 * Adapted from Streamlit log_session logic in helper_app.py
 */

// Generate unique session ID
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
};

// Get or create session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('lotwizard_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem('lotwizard_session_id', sessionId);
  }
  return sessionId;
};

// Get user's IP address (approximate)
const getUserIP = async () => {
  // In development, skip IP detection to avoid CORS issues
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'Development IP (localhost)';
  }
  
  try {
    // Try primary IP service
    const response = await fetch('https://api64.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Could not fetch IP address:', error);
    return 'Unknown IP';
  }
};

// Get browser information
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const language = navigator.language;
  const cookieEnabled = navigator.cookieEnabled;
  const onLine = navigator.onLine;
  
  // Parse browser info
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  
  if (userAgent.includes('Chrome')) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/([0-9.]+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/([0-9.]+)/);
    browserVersion = match ? match[1] : 'Unknown';
  } else if (userAgent.includes('Safari')) {
    browserName = 'Safari';
    const match = userAgent.match(/Safari\/([0-9.]+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  
  return {
    browserName,
    browserVersion,
    platform,
    language,
    cookieEnabled,
    onLine,
    userAgent
  };
};

// Get screen information
const getScreenInfo = () => {
  // Use window.screen to avoid ESLint no-restricted-globals warning
  const screenObj = window.screen;
  return {
    resolution: `${screenObj.width}x${screenObj.height}`,
    availableResolution: `${screenObj.availWidth}x${screenObj.availHeight}`,
    colorDepth: screenObj.colorDepth,
    pixelDepth: screenObj.pixelDepth
  };
};

// Always log authentication events - no duplicate prevention for session logging
const isAuthenticationLogged = () => {
  return false; // Always allow session logging
};

// Mark authentication as logged (for tracking purposes)
const markAuthenticationAsLogged = () => {
  // No-op - we always allow session logging
  console.log('📊 Authentication logged at:', new Date().toISOString());
};

// Session Logger Class
class SessionLogger {
  constructor() {
    this.baseUrl = this.getApiBaseUrl();
    this.sessionId = getSessionId();
    this.ipAddress = null;
    this.browserInfo = getBrowserInfo();
    this.screenInfo = getScreenInfo();
  }

  // Get API base URL based on environment
  getApiBaseUrl() {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalhost) {
      // Development - direct connection to remote backend
      return 'http://64.225.20.106:8008/api';
    } else {
      // Production - nginx proxy
      return '/backend';
    }
  }

  // Initialize IP address (async)
  async initializeIP() {
    if (!this.ipAddress) {
      this.ipAddress = await getUserIP();
    }
  }

  // Log session to backend API
  async logSession(authToken = null) {
    try {
      // Always log authentication events - removed duplicate prevention
      console.log('📊 Logging new authentication session...');

      // Initialize IP if not already done
      await this.initializeIP();

      // Prepare session data
      const sessionData = {
        session_id: this.sessionId,
        ip_address: this.ipAddress,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        referrer: document.referrer,
        browser_info: this.browserInfo,
        screen_resolution: this.screenInfo.resolution,
        platform: 'react_web',
        timestamp: new Date().toISOString(),
        // Additional metadata
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen_info: this.screenInfo
      };

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add authorization header if token provided
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      console.log('📊 Logging session data:', {
        session_id: sessionData.session_id,
        ip_address: sessionData.ip_address,
        browser: `${this.browserInfo.browserName} ${this.browserInfo.browserVersion}`,
        platform: this.browserInfo.platform
      });

      // Send to backend API
      const response = await fetch(`${this.baseUrl}/log-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify(sessionData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Session logged successfully:', result.message);

        // Mark authentication as logged (for tracking)
        markAuthenticationAsLogged();
        
        return {
          status: 'success',
          sessionId: this.sessionId,
          timestamp: result.timestamp
        };
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.warn('⚠️ Session logging failed:', response.status, errorData);
        
        return {
          status: 'error',
          error: errorData.error || `HTTP ${response.status}`
        };
      }

    } catch (error) {
      console.error('❌ Error logging session:', error);
      return {
        status: 'error',
        error: error.message
      };
    }
  }

  // Log session with authentication (main method)
  async logUserSession(authToken) {
    if (!authToken) {
      console.warn('⚠️ No auth token provided for session logging');
      return { status: 'no_auth' };
    }

    return await this.logSession(authToken);
  }

  // Get current session info
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      ipAddress: this.ipAddress,
      browserInfo: this.browserInfo,
      screenInfo: this.screenInfo,
      isLogged: false // Always show as not logged to allow logging
    };
  }

  // Reset session (for testing or logout)
  resetSession() {
    localStorage.removeItem('lotwizard_session_id');
    sessionStorage.removeItem('lotwizard_last_logged_auth');
    this.sessionId = generateSessionId();
    localStorage.setItem('lotwizard_session_id', this.sessionId);
    console.log('🔄 Session reset, new session ID:', this.sessionId);
  }

  // Clear session logged flag (for logout) - now deprecated but kept for compatibility
  clearSessionLogged() {
    console.log('🔄 Session logging now tracks every authentication - no flag clearing needed');
  }
}

// Create singleton instance
const sessionLogger = new SessionLogger();

// Export both the class and instance
export { SessionLogger, sessionLogger };
export default sessionLogger;